import React, { Suspense } from 'react';
import { Routes, Route, Navigate, BrowserRouter as Router } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { AuthProvider } from './context/AuthContext';
import { ModalProvider } from './contexts/ModalContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';

// Core pages (loaded immediately)
import LandingPage from './pages/LandingPage';

// Lazy loaded pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const AccessibilityPage = React.lazy(() => import('./pages/AccessibilityPage'));
const AbuseReportPage = React.lazy(() => import('./pages/AbuseReportPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const EnhancedDashboardPage = React.lazy(() => import('./pages/EnhancedDashboardPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const SubmissionDetailsPage = React.lazy(() => import('./pages/SubmissionDetailsPage'));
const EndpointsPage = React.lazy(() => import('./pages/EndpointsPage'));
const FormBuilderPage = React.lazy(() => import('./pages/FormBuilderPage'));
const FormDisplayPage = React.lazy(() => import('./pages/FormDisplayPage'));
const FormSubmissionsPage = React.lazy(() => import('./pages/FormSubmissionsPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const DocsPage = React.lazy(() => import('./pages/DocsPage'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const TicketDetailsPage = React.lazy(() => import('./pages/TicketDetailsPage'));

// Blog pages (lazy loaded)
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const HtmlFormBackendSetupGuide = React.lazy(() => import('./pages/blog/html-form-backend-setup-guide'));
const ContactFormSecurityBestPractices = React.lazy(() => import('./pages/blog/contact-form-security-best-practices'));
const JamstackFormsCompleteGuide = React.lazy(() => import('./pages/blog/jamstack-forms-complete-guide'));
const AddingContactFormsStaticSites = React.lazy(() => import('./pages/blog/adding-contact-forms-static-sites'));
const StaticSiteFormsSolutionsComparison = React.lazy(() => import('./pages/blog/static-site-forms-solutions-comparison'));
const FormCareVsBuildingYourOwnBackend = React.lazy(() => import('./pages/blog/formcare-vs-building-your-own-backend'));
const PreventingFormSpamBotAttacks = React.lazy(() => import('./pages/blog/preventing-form-spam-bot-attacks'));
const ContactFormGDPRComplianceGuide = React.lazy(() => import('./pages/blog/contact-form-gdpr-compliance-guide'));
const FormValidationBestPractices = React.lazy(() => import('./pages/blog/form-validation-best-practices'));
const ConnectingFluxUIToFormCareGuide = React.lazy(() => import('./pages/blog/connecting-flux-ui-to-formcare-guide'));
const NextJSContactFormWithoutFirebase = React.lazy(() => import('./pages/blog/nextjs-contact-form-without-firebase'));
const ReactFormSubmissionsNoServer = React.lazy(() => import('./pages/blog/react-form-submissions-no-server'));
const AWSLambdaFormEndpointAlternative = React.lazy(() => import('./pages/blog/aws-lambda-form-endpoint-alternative'));
const FormCareDataPrivacySecurity = React.lazy(() => import('./pages/blog/formcare-data-privacy-security'));
const WebflowFileUploadsGuide = React.lazy(() => import('./pages/blog/webflow-file-uploads-guide'));
const FormCareCustomerSupportService = React.lazy(() => import('./pages/blog/formcare-customer-support-service'));
const CustomNotificationEmailFormManagement = React.lazy(() => import('./pages/blog/custom-notification-email-form-management'));
const FormCareDesignUpdate = React.lazy(() => import('./pages/blog/formcare-design-update'));
const WixFormCareIntegrationGuide = React.lazy(() => import('./pages/blog/wix-formcare-integration-guide'));
const ChatGptCloudflareLandingPageGuide = React.lazy(() => import('./pages/blog/chatgpt-cloudflare-landing-page-guide'));
const FormCareAntispamFormProtection = React.lazy(() => import('./pages/blog/formcare-antispam-form-protection'));
const FormCareProductUpdateSimplerSignup = React.lazy(() => import('./pages/blog/formcare-product-update-simpler-signup'));

// Components
import LoadingFallback from './components/LoadingFallback';
import { BlogLoadingFallback, BlogListingFallback } from './components/blog';
import { LazyLoadErrorBoundary } from './utils/performanceMonitor.jsx';
import { ToastProvider } from './contexts/ToastContext';

function AppContent() {
  return (
    <div className="App">
      <ScrollToTop />
      <Navbar />
      <main className="app-main">
        <Routes>
          {/* Core pages (no lazy loading for landing) */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense>} />
          <Route path="/register" element={<Suspense fallback={<LoadingFallback />}><RegisterPage /></Suspense>} />
          <Route path="/forgot-password" element={<Suspense fallback={<LoadingFallback />}><ForgotPasswordPage /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<LoadingFallback />}><ResetPasswordPage /></Suspense>} />
          <Route path="/verify-email" element={<Suspense fallback={<LoadingFallback />}><VerifyEmailPage /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<LoadingFallback />}><TermsPage /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<LoadingFallback />}><PrivacyPage /></Suspense>} />
          <Route path="/accessibility" element={<Suspense fallback={<LoadingFallback />}><AccessibilityPage /></Suspense>} />
          <Route path="/abuse" element={
            <Suspense fallback={<LoadingFallback message="Loading report form..." />}>
              <AbuseReportPage />
            </Suspense>
          } />
          <Route path="/html-form-backend" element={<Navigate to="/#compare" replace />} />
          
          {/* Blog pages with blog-specific loading */}
          <Route path="/blog" element={
            <LazyLoadErrorBoundary componentName="BlogPage">
              <Suspense fallback={<BlogListingFallback />}>
                <BlogPage />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/html-form-backend-setup-guide" element={
            <LazyLoadErrorBoundary componentName="HtmlFormBackendSetupGuide">
              <Suspense fallback={<BlogLoadingFallback />}>
                <HtmlFormBackendSetupGuide />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/contact-form-security-best-practices" element={
            <LazyLoadErrorBoundary componentName="ContactFormSecurityBestPractices">
              <Suspense fallback={<BlogLoadingFallback />}>
                <ContactFormSecurityBestPractices />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/jamstack-forms-complete-guide" element={
            <LazyLoadErrorBoundary componentName="JamstackFormsCompleteGuide">
              <Suspense fallback={<BlogLoadingFallback />}>
                <JamstackFormsCompleteGuide />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/adding-contact-forms-static-sites" element={
            <LazyLoadErrorBoundary componentName="AddingContactFormsStaticSites">
              <Suspense fallback={<BlogLoadingFallback />}>
                <AddingContactFormsStaticSites />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/static-site-forms-solutions-comparison" element={
            <LazyLoadErrorBoundary componentName="StaticSiteFormsSolutionsComparison">
              <Suspense fallback={<BlogLoadingFallback />}>
                <StaticSiteFormsSolutionsComparison />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/formcare-vs-building-your-own-backend" element={
            <LazyLoadErrorBoundary componentName="FormCareVsBuildingYourOwnBackend">
              <Suspense fallback={<BlogLoadingFallback />}>
                <FormCareVsBuildingYourOwnBackend />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/preventing-form-spam-bot-attacks" element={
            <LazyLoadErrorBoundary componentName="PreventingFormSpamBotAttacks">
              <Suspense fallback={<BlogLoadingFallback />}>
                <PreventingFormSpamBotAttacks />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/contact-form-gdpr-compliance-guide" element={
            <LazyLoadErrorBoundary componentName="ContactFormGDPRComplianceGuide">
              <Suspense fallback={<BlogLoadingFallback />}>
                <ContactFormGDPRComplianceGuide />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/form-validation-best-practices" element={
            <LazyLoadErrorBoundary componentName="FormValidationBestPractices">
              <Suspense fallback={<BlogLoadingFallback />}>
                <FormValidationBestPractices />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/connecting-flux-ui-to-formcare-guide" element={
            <LazyLoadErrorBoundary componentName="ConnectingFluxUIToFormCareGuide">
              <Suspense fallback={<BlogLoadingFallback />}>
                <ConnectingFluxUIToFormCareGuide />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/nextjs-contact-form-without-firebase" element={
            <LazyLoadErrorBoundary componentName="NextJSContactFormWithoutFirebase">
              <Suspense fallback={<BlogLoadingFallback />}>
                <NextJSContactFormWithoutFirebase />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/react-form-submissions-no-server" element={
            <LazyLoadErrorBoundary componentName="ReactFormSubmissionsNoServer">
              <Suspense fallback={<BlogLoadingFallback />}>
                <ReactFormSubmissionsNoServer />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/aws-lambda-form-endpoint-alternative" element={
            <LazyLoadErrorBoundary componentName="AWSLambdaFormEndpointAlternative">
              <Suspense fallback={<BlogLoadingFallback />}>
                <AWSLambdaFormEndpointAlternative />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/formcare-data-privacy-security" element={
            <LazyLoadErrorBoundary componentName="FormCareDataPrivacySecurity">
              <Suspense fallback={<BlogLoadingFallback />}>
                <FormCareDataPrivacySecurity />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/webflow-file-uploads-guide" element={
            <LazyLoadErrorBoundary componentName="WebflowFileUploadsGuide">
              <Suspense fallback={<BlogLoadingFallback />}>
                <WebflowFileUploadsGuide />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/formcare-customer-support-service" element={
            <LazyLoadErrorBoundary componentName="FormCareCustomerSupportService">
              <Suspense fallback={<BlogLoadingFallback />}>
                <FormCareCustomerSupportService />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/custom-notification-email-form-management" element={
            <LazyLoadErrorBoundary componentName="CustomNotificationEmailFormManagement">
              <Suspense fallback={<BlogLoadingFallback />}>
                <CustomNotificationEmailFormManagement />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/formcare-design-update" element={
            <LazyLoadErrorBoundary componentName="FormCareDesignUpdate">
              <Suspense fallback={<BlogLoadingFallback />}>
                <FormCareDesignUpdate />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/wix-formcare-integration-guide" element={
            <LazyLoadErrorBoundary componentName="WixFormCareIntegrationGuide">
              <Suspense fallback={<BlogLoadingFallback />}>
                <WixFormCareIntegrationGuide />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/chatgpt-cloudflare-landing-page-guide" element={
            <LazyLoadErrorBoundary componentName="ChatGptCloudflareLandingPageGuide">
              <Suspense fallback={<BlogLoadingFallback />}>
                <ChatGptCloudflareLandingPageGuide />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/formcare-antispam-form-protection" element={
            <LazyLoadErrorBoundary componentName="FormCareAntispamFormProtection">
              <Suspense fallback={<BlogLoadingFallback />}>
                <FormCareAntispamFormProtection />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          <Route path="/blog/formcare-product-update-simpler-signup" element={
            <LazyLoadErrorBoundary componentName="FormCareProductUpdateSimplerSignup">
              <Suspense fallback={<BlogLoadingFallback />}>
                <FormCareProductUpdateSimplerSignup />
              </Suspense>
            </LazyLoadErrorBoundary>
          } />
          
          {/* Dashboard and app pages with standard loading */}
          <Route path="/docs" element={
            <Suspense fallback={<LoadingFallback message="Loading documentation..." />}>
              <DocsPage />
            </Suspense>
          } />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Navigate to="/endpoints?new=1" replace />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading dashboard..." />}>
                <EnhancedDashboardPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/endpoints" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading endpoints..." />}>
                <EndpointsPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/submissions/:submissionId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading submission..." />}>
                <SubmissionDetailsPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/forms/:formId/submissions" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading submissions..." />}>
                <FormSubmissionsPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading settings..." />}>
                <SettingsPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading billing..." />}>
                <BillingPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading support..." />}>
                <SupportPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/support/:ticketId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading ticket details..." />}>
                <TicketDetailsPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/forms/new" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading form builder..." />}>
                <FormBuilderPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/forms/:formId/edit" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading form builder..." />}>
                <FormBuilderPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/forms/:formId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback message="Loading form..." />}>
                <FormDisplayPage />
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* Fallback Route */}
          <Route path="*" element={
            <Suspense fallback={<LoadingFallback />}>
              <NotFoundPage />
            </Suspense>
          } />
        </Routes>
      </main>
    </div>
  );
}

// Main App component that includes the AuthProvider and Router
function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <ModalProvider>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </ModalProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App; 